import React, { useState } from 'react';
import {
  Box, Card, Typography, Button, CircularProgress, Alert,
  TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '@services/api/reports';
import { useDashboardFilters } from '@hooks/useDashboardFilters';
import { useDashboardSummary, useDashboardByOfficer, useDashboardScopeSummary } from '@hooks/useDashboard';
import { DashboardFilterBar } from '@components/DashboardFilterBar';
import { DashboardKPIRow } from '@components/DashboardKPIRow';

const COLORS = {
  bg: '#0f1419', card: '#1a2332', cardBorder: '#2a3444',
  text: '#ffffff', muted: '#8b95a5', orange: '#FF6B4A', green: '#00D084', red: '#FF4D4F',
};

const REPORT_TYPES = [
  { key: 'daily_breakdown', title: 'Daily registration breakdown', subtitle: 'Per-location output against target, for the selected period.' },
  { key: 'officer_performance', title: 'Officer performance', subtitle: 'Every officer ranked by achievement, with silent days flagged.' },
  { key: 'missed_target', title: 'Missed-target officers', subtitle: 'Officers below target — the follow-up list. Silent first.' },
  { key: 'location_conversion', title: 'Location conversion', subtitle: "Share of each location's pool converted so far. Worst first." },
  { key: 'officer_coverage', title: 'Officer coverage', subtitle: 'Assigned vs reporting officers by district.' },
  { key: 'units_without_officer', title: 'Units without an officer', subtitle: 'Their pool counts against conversion but nobody is working it.' },
];

function toCSV(rows: any[]): string {
  if (rows.length === 0) return '';
  const headers = Object.keys(rows[0]);
  const lines = [headers.join(',')];
  for (const row of rows) {
    lines.push(headers.map((h) => {
      const v = row[h];
      const s = v === null || v === undefined ? '' : String(v);
      return s.includes(',') ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(','));
  }
  return lines.join('\n');
}

function downloadCSV(rows: any[], filename: string) {
  const csv = toCSV(rows);
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const COLUMN_SETS: Record<string, { key: string; label: string; align?: 'right' }[]> = {
  daily_breakdown: [
    { key: 'location', label: 'LOCATION' }, { key: 'division', label: 'DIVISION' }, { key: 'district', label: 'SUBCOUNTY' },
    { key: 'target', label: 'TARGET', align: 'right' }, { key: 'actual', label: 'ACTUAL', align: 'right' }, { key: 'achieved_percentage', label: 'ACHIEVED', align: 'right' },
  ],
  officer_performance: [
    { key: 'officer_name', label: 'OFFICER' }, { key: 'role', label: 'ROLE' },
    { key: 'target', label: 'TARGET', align: 'right' }, { key: 'actual', label: 'ACTUAL', align: 'right' }, { key: 'achieved_percentage', label: 'ACHIEVED', align: 'right' },
  ],
  missed_target: [
    { key: 'officer_name', label: 'OFFICER' }, { key: 'role', label: 'ROLE' },
    { key: 'target', label: 'TARGET', align: 'right' }, { key: 'actual', label: 'ACTUAL', align: 'right' }, { key: 'achieved_percentage', label: 'ACHIEVED', align: 'right' },
  ],
  location_conversion: [
    { key: 'location', label: 'LOCATION' }, { key: 'district', label: 'SUBCOUNTY' },
    { key: 'pool', label: 'POOL', align: 'right' }, { key: 'registered', label: 'REGISTERED', align: 'right' },
    { key: 'remaining', label: 'REMAINING', align: 'right' }, { key: 'converted_percentage', label: 'CONVERTED', align: 'right' },
  ],
  officer_coverage: [
    { key: 'district', label: 'SUBCOUNTY' }, { key: 'assigned', label: 'ASSIGNED', align: 'right' }, { key: 'reporting', label: 'REPORTING', align: 'right' },
  ],
  units_without_officer: [
    { key: 'location', label: 'LOCATION' }, { key: 'division', label: 'DIVISION' }, { key: 'district', label: 'SUBCOUNTY' },
    { key: 'pool', label: 'POOL', align: 'right' },
  ],
};

const formatCell = (col: string, value: any, row: any) => {
  if (value === null || value === undefined) return '—';
  if (col === 'achieved_percentage' || col === 'converted_percentage') {
    if (row.actual === 0 && col === 'achieved_percentage') return <span style={{ color: COLORS.muted }}>—</span>;
    const pct = Number(value);
    return <span style={{ color: pct >= 100 ? COLORS.green : pct > 0 ? COLORS.orange : COLORS.muted, fontWeight: 700 }}>{pct.toFixed(1)}%</span>;
  }
  if (col === 'role') return String(value).replace('_', ' ');
  if (typeof value === 'number') return value.toLocaleString();
  return String(value);
};

export const ReportsPage: React.FC = () => {
  const filters = useDashboardFilters();
  const { selectedCounty, selectedDistrict, selectedDivision, selectedLocation, period } = filters;
  const [selectedReport, setSelectedReport] = useState('daily_breakdown');

  const summary = useDashboardSummary();
  const officerMetrics = useDashboardByOfficer();
  const scopeSummary = useDashboardScopeSummary({
    county: selectedCounty || undefined, district: selectedDistrict || undefined,
    division: selectedDivision || undefined, location: selectedLocation || undefined,
  });
  const officers = officerMetrics.data || [];

  const reportQuery = useQuery({
    queryKey: ['reports', selectedReport, selectedCounty, selectedDistrict, selectedDivision, selectedLocation, period],
    queryFn: () => reportsApi.getReports(selectedReport, {
      county: selectedCounty || undefined, district: selectedDistrict || undefined,
      division: selectedDivision || undefined, location: selectedLocation || undefined, period,
    }),
    staleTime: 60000,
  });

  const activeReport = REPORT_TYPES.find((r) => r.key === selectedReport)!;
  const columns = COLUMN_SETS[selectedReport];
  const data = reportQuery.data;
  const rows = data?.rows || [];

  const handleRefresh = () => reportQuery.refetch();

  return (
    <Box sx={{ pb: 4, color: COLORS.text }}>
      <DashboardFilterBar filters={filters} onRefresh={handleRefresh} />
      <DashboardKPIRow
        scopeData={scopeSummary.data}
        totalOfficers={summary.data?.total_officers}
        belowTargetCount={(officers as any[]).filter((o: any) => (o.registrations_today || 0) < 50).length}
      />

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        {/* Sidebar: report type list */}
        <Box sx={{ width: 270, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {REPORT_TYPES.map((r) => {
            const active = r.key === selectedReport;
            return (
              <Card
                key={r.key}
                onClick={() => setSelectedReport(r.key)}
                sx={{
                  p: 1.5, cursor: 'pointer',
                  backgroundColor: active ? 'rgba(0, 208, 132, 0.08)' : COLORS.card,
                  border: `1px solid ${active ? COLORS.green : COLORS.cardBorder}`,
                  '&:hover': { borderColor: COLORS.green },
                }}
              >
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: active ? COLORS.green : COLORS.text, mb: 0.5 }}>
                  {r.title}
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: COLORS.muted, lineHeight: 1.4 }}>
                  {r.subtitle}
                </Typography>
              </Card>
            );
          })}
        </Box>

        {/* Main: table for the selected report */}
        <Card sx={{ flex: 1, backgroundColor: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, minWidth: 0 }}>
          <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: `1px solid ${COLORS.cardBorder}` }}>
            <Box>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 700 }}>{activeReport.title}</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: COLORS.muted, mt: 0.25 }}>
                {data ? (
                  <>
                    {data.period.replace(/_/g, ' ')} · {data.total_rows.toLocaleString()} rows · showing first {rows.length} · generated{' '}
                    {new Date(data.generated_at).toLocaleString('en-KE', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </>
                ) : 'Loading…'}
              </Typography>
            </Box>
            <Button
              size="small" variant="outlined"
              disabled={rows.length === 0}
              onClick={() => downloadCSV(rows, `${selectedReport}-${Date.now()}.csv`)}
              sx={{ borderColor: COLORS.cardBorder, color: COLORS.muted, textTransform: 'none', '&:hover': { borderColor: COLORS.green, color: COLORS.green } }}
            >
              Export CSV
            </Button>
          </Box>

          {reportQuery.isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: COLORS.green }} /></Box>
          ) : reportQuery.error ? (
            <Alert severity="error" sx={{ m: 2 }}>Failed to load report</Alert>
          ) : rows.length === 0 ? (
            <Typography sx={{ p: 4, color: COLORS.muted, textAlign: 'center' }}>No data for this selection</Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 640 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {columns.map((c) => (
                      <TableCell
                        key={c.key} align={c.align}
                        sx={{ backgroundColor: '#251c0c', color: COLORS.muted, fontSize: '0.68rem', fontWeight: 700, letterSpacing: 0.4, borderBottom: `1px solid ${COLORS.cardBorder}` }}
                      >
                        {c.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row: any, idx: number) => (
                    <TableRow key={idx} sx={{ backgroundColor: idx % 2 === 1 ? 'rgba(26,35,50,0.4)' : COLORS.card }}>
                      {columns.map((c) => (
                        <TableCell key={c.key} align={c.align} sx={{ color: COLORS.text, fontSize: '0.78rem', py: 0.6, border: 'none' }}>
                          {row.silent && c.key === 'officer_name' && <span style={{ color: COLORS.red, fontWeight: 700, marginRight: 6 }}>●</span>}
                          {formatCell(c.key, row[c.key], row)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      </Box>
    </Box>
  );
};
